import { ChangeEvent, FC, useEffect, useState } from 'react';
import { ActionMeta, MultiValue, SingleValue } from 'react-select';
import { useSelector } from 'react-redux';
import { Kushki } from '@kushki/js';
import Input from '../../../../../common/input/input';
import Select, { SingleSelect, formatResponseForSelect } from '../../../../../common/select/select';
import { LATINA_COUNTRY_CODE } from '../../../../../constants/latina-country';
import translation from '../../../../../languages';
import {
    TRANSFER_FORM_NAMES,
    TRANSFER_SELECT_BANK_STYLES,
    TRANSFER_SELECT_STYLES,
    TRANSFER_USER_DATA
} from '../constants/kushki';
import { KushkiTransferType } from '../types/kushki-types';
import { getDocumentTypeData } from '../utils/kushki';
import { getKushkiBankList } from '../kushki-process/transfer';

type KushkiTransferProps = TranslationProps & {
    formErrors: string[];
    onTransferDataChange: (name: string, value: string) => void;
    region: LATINA_COUNTRY_CODE;
    transferData: KushkiTransferType;
};

type BankListSelectedDataType = {
    bankId: SingleSelect | null;
    documentType: SingleSelect;
    userType: SingleSelect;
};

const KushkiTransfer: FC<KushkiTransferProps> = ({
    onTransferDataChange,
    transferData,
    region,
    t,
    formErrors
}) => {
    const { paymentOptionsData } = useSelector((state: RootState) => state.payment);
    const [bankList, setBankList] = useState<SingleSelect[] | null>(null);
    const [selectData, setSelectData] = useState<BankListSelectedDataType>({
        bankId: null,
        documentType: getDocumentTypeData(region)[0],
        userType: TRANSFER_USER_DATA[0]
    });

    useEffect(() => {
        if (!paymentOptionsData?.loaded) {
            return;
        }

        if (paymentOptionsData.userCountry === 'CO') {
            const kushkiCheckout = new Kushki({
                inTestEnvironment: paymentOptionsData.kushkiEnv.inTestEnvironment,
                merchantId: paymentOptionsData.kushkiEnv.merchantId
            });

            getKushkiBankList(kushkiCheckout)
                .then((data) => {
                    const formattedBankList = formatResponseForSelect(data, 'name', 'code');

                    setBankList(formattedBankList);
                    setSelectData({
                        bankId: formattedBankList?.[0],
                        documentType: getDocumentTypeData(region)[0],
                        userType: TRANSFER_USER_DATA[0]
                    });
                });
        }
    }, [paymentOptionsData]);

    const handleSelectChange = (
        newValue: SingleValue<SingleSelect> | MultiValue<SingleSelect>,
        actionMeta: ActionMeta<SingleSelect>
    ) => {
        if (newValue) {
            setSelectData((prev) => ({ ...prev, [actionMeta.name!]: newValue }));
            // @ts-ignore
            onTransferDataChange(actionMeta.name!, newValue.value);
        }
    };

    const handleUpdateForm = (e: ChangeEvent<HTMLInputElement>) => {
        onTransferDataChange(e.target.name, e.target.value);
    };

    return (
        <>
            <div>
                {Boolean(bankList?.length && selectData.bankId) && (
                    <div className='payment-flex mb-4'>
                        <div>
                            <span>{t('payment.kushki.transfer.userType')}</span>
                            <Select
                                customStyle={TRANSFER_SELECT_BANK_STYLES}
                                hasError={formErrors.includes(TRANSFER_FORM_NAMES.BANK_ID)}
                                name={TRANSFER_FORM_NAMES.BANK_ID}
                                options={bankList ?? []}
                                placeholder='documentType'
                                value={selectData.bankId}
                                onChange={handleSelectChange}
                            />
                        </div>
                    </div>
                )}
                <div className='payment-flex mb-4'>
                    <div className='mr-3'>
                        <span>{t('payment.kushki.transfer.userType')}</span>
                        <Select
                            className='designed-select'
                            customStyle={TRANSFER_SELECT_STYLES}
                            name={TRANSFER_FORM_NAMES.USER_TYPE}
                            options={TRANSFER_USER_DATA}
                            placeholder='userType'
                            value={selectData.userType}
                            onChange={handleSelectChange}
                        />
                    </div>
                    <div>
                        <div>
                            <span>{t('payment.kushki.documentType')}</span>
                            <Select
                                className='designed-select'
                                customStyle={TRANSFER_SELECT_STYLES}
                                name={TRANSFER_FORM_NAMES.DOCUMENT_TYPE}
                                options={getDocumentTypeData(region)}
                                placeholder='documentType'
                                value={selectData.documentType}
                                onChange={handleSelectChange}
                            />
                        </div>
                    </div>
                </div>
                <div className='payment-flex mb-4'>
                    <div>
                        <span>{t('payment.kushki.documentNumber')}</span>
                        <Input
                            className='payment-input'
                            error={formErrors.includes(TRANSFER_FORM_NAMES.DOCUMENT_NUMBER)}
                            maxLength={20}
                            name={TRANSFER_FORM_NAMES.DOCUMENT_NUMBER}
                            type='text'
                            value={transferData.documentNumber}
                            onChange={handleUpdateForm}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const translate = translation('payment');

export default translate(KushkiTransfer);
